import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { HEALTH_TIPS } from '@/mocks/jeux-data';

type GridSize = 3 | 4 | 5 | 7;

const PUZZLE_IMAGES = [
  'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/games/puzzle-1.webp',
  'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/games/puzzle-2.webp',
  'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/games/puzzle-3.webp',
  'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/games/puzzle-4.webp',
];

export default function PuzzleClassique() {
  const navigate = useNavigate();
  const { startGame, endGame, gameScore, gameTime, gameErrors, updateScore, updateErrors } = useGame();
  const [gridSize, setGridSize] = useState<GridSize | null>(null);
  const [pieces, setPieces] = useState<number[]>([]);
  const [placedPieces, setPlacedPieces] = useState<(number | null)[]>([]);
  const [draggedFrom, setDraggedFrom] = useState<'bank' | 'grid' | null>(null);
  const [draggedSourceIdx, setDraggedSourceIdx] = useState<number | null>(null);
  const [showGameOver, setShowGameOver] = useState(false);
  const [imageIdx] = useState(() => Math.floor(Math.random() * PUZZLE_IMAGES.length));
  const [imageLoaded, setImageLoaded] = useState(false);
  const [usedTipIdx, setUsedTipIdx] = useState(() => Math.floor(Math.random() * HEALTH_TIPS.length));

  useEffect(() => {
    if (!gridSize) return;
    setImageLoaded(false);
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageLoaded(true);
    img.src = PUZZLE_IMAGES[imageIdx];
  }, [gridSize, imageIdx]);

  const handleStart = (size: GridSize) => {
    setGridSize(size);
    const total = size * size;
    const shuffled = Array.from({ length: total }, (_, i) => i).sort(() => Math.random() - 0.5);
    setPieces(shuffled);
    setPlacedPieces(Array(total).fill(null));
    setShowGameOver(false);
    setUsedTipIdx(Math.floor(Math.random() * HEALTH_TIPS.length));
    startGame('puzzle-classique');
  };

  const handleDragStartBank = (idx: number) => {
    setDraggedFrom('bank');
    setDraggedSourceIdx(idx);
  };

  const handleDragStartGrid = (idx: number) => {
    setDraggedFrom('grid');
    setDraggedSourceIdx(idx);
  };

  const handleDropOnBank = useCallback(() => {
    if (draggedFrom === 'grid' && draggedSourceIdx !== null && placedPieces[draggedSourceIdx] !== null) {
      const pieceValue = placedPieces[draggedSourceIdx]!;
      const newPlaced = [...placedPieces];
      newPlaced[draggedSourceIdx] = null;
      const newPieces = [...pieces];
      const firstEmpty = newPieces.findIndex((p) => p === -1);
      if (firstEmpty >= 0) {
        newPieces[firstEmpty] = pieceValue;
      } else {
        newPieces.push(pieceValue);
      }
      setPieces(newPieces);
      setPlacedPieces(newPlaced);
      updateScore(gameScore - 5);
    }
    setDraggedFrom(null);
    setDraggedSourceIdx(null);
  }, [draggedFrom, draggedSourceIdx, placedPieces, pieces, gameScore, updateScore]);

  const handleDropOnGrid = useCallback(
    (targetIdx: number) => {
      if (draggedFrom === null || draggedSourceIdx === null) return;

      const newPieces = [...pieces];
      const newPlaced = [...placedPieces];

      if (draggedFrom === 'bank') {
        const pieceValue = newPieces[draggedSourceIdx];

        if (newPlaced[targetIdx] !== null) {
          const existingGridPiece = newPlaced[targetIdx]!;
          newPlaced[targetIdx] = pieceValue;
          newPieces[draggedSourceIdx] = existingGridPiece;
          updateScore(gameScore + 5);
          updateErrors(gameErrors + 1);
        } else {
          newPlaced[targetIdx] = pieceValue;
          newPieces[draggedSourceIdx] = -1;
          updateScore(gameScore + 10);
        }
      } else if (draggedFrom === 'grid' && draggedSourceIdx !== targetIdx) {
        const temp = newPlaced[draggedSourceIdx];
        newPlaced[draggedSourceIdx] = newPlaced[targetIdx];
        newPlaced[targetIdx] = temp;
        updateScore(gameScore + 2);
      } else {
        setDraggedFrom(null);
        setDraggedSourceIdx(null);
        return;
      }

      setPieces(newPieces);
      setPlacedPieces(newPlaced);
      setDraggedFrom(null);
      setDraggedSourceIdx(null);

      const allFilled = newPlaced.every((p) => p !== null);
      const allCorrect = newPlaced.every((p, i) => p === i);

      if (allFilled && allCorrect) {
        const finalScore = gameScore + 200 + Math.max(0, 600 - gameTime);
        updateScore(finalScore);
        endGame(finalScore, gameTime, gameErrors);
        setShowGameOver(true);
      }
    },
    [draggedFrom, draggedSourceIdx, pieces, placedPieces, gameScore, gameTime, gameErrors, updateScore, updateErrors, endGame],
  );

  const handleClickGridPiece = (idx: number) => {
    if (placedPieces[idx] === null) return;
    const pieceValue = placedPieces[idx]!;
    const newPlaced = [...placedPieces];
    newPlaced[idx] = null;
    const newPieces = [...pieces];
    const firstEmpty = newPieces.findIndex((p) => p === -1);
    if (firstEmpty >= 0) {
      newPieces[firstEmpty] = pieceValue;
    } else {
      newPieces.push(pieceValue);
    }
    setPieces(newPieces);
    setPlacedPieces(newPlaced);
  };

  const handleReplay = () => {
    if (gridSize) handleStart(gridSize);
  };

  const handleQuit = () => {
    endGame(gameScore, gameTime, gameErrors);
    navigate('/jeux', { replace: true });
  };

  if (!gridSize) {
    return (
      <GameWrapper gameName="Puzzle Classique" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-puzzle-line text-3xl text-accent-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Puzzle Classique</h2>
            <p className="text-foreground-600 text-sm mb-6">Choisis le nombre de pieces :</p>
            <div className="grid grid-cols-2 gap-3">
              {([3, 4, 5, 7] as GridSize[]).map((s) => (
                <button
                  key={s}
                  onClick={() => handleStart(s)}
                  className="py-3 px-4 bg-secondary-100 hover:bg-secondary-200 rounded-xl text-sm font-medium text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {s}&times;{s} ({s * s} pieces)
                </button>
              ))}
            </div>
          </div>
        </div>
      </GameWrapper>
    );
  }

  const total = gridSize * gridSize;
  const remainingCount = pieces.filter((p) => p !== -1).length;
  const allFilled = placedPieces.every((p) => p !== null);
  const placedCount = placedPieces.filter((p) => p !== null).length;

  if (!imageLoaded) {
    return (
      <GameWrapper gameName="Puzzle Classique" onQuit={handleQuit}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-background-200 border-t-accent-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-foreground-600 text-sm">Chargement de l&apos;image...</p>
          </div>
        </div>
      </GameWrapper>
    );
  }

  return (
    <GameWrapper gameName="Puzzle Classique" onQuit={handleQuit}>
      <div className="flex-1 flex flex-col lg:flex-row gap-4 p-4 max-w-5xl mx-auto w-full">
        <div className="lg:hidden flex items-center gap-2 text-sm text-foreground-600 mb-1">
          <i className="ri-checkbox-circle-line text-accent-500"></i>
          <span>{placedCount}/{total} pieces placees</span>
          {allFilled && !placedPieces.every((p, i) => p === i) && (
            <span className="text-amber-600 text-xs ml-2">- Certaines pieces sont mal placees !</span>
          )}
        </div>

        <div className="flex-1 flex flex-col items-center">
          <div className="hidden lg:flex items-center gap-2 text-sm text-foreground-600 mb-3 self-start">
            <i className="ri-grid-line"></i>
            <span>{placedCount}/{total} pieces placees</span>
            {allFilled && !placedPieces.every((p, i) => p === i) && (
              <span className="text-amber-600 text-xs ml-2 font-medium">- Certaines pieces sont mal placees !</span>
            )}
          </div>
          <div
            className="grid gap-1 p-2 bg-background-200/50 rounded-2xl w-full"
            style={{ gridTemplateColumns: `repeat(${gridSize}, 1fr)` }}
          >
            {placedPieces.map((piece, idx) => (
              <div
                key={idx}
                className={`aspect-square rounded-lg border-2 flex items-center justify-center transition-all ${
                  piece !== null
                    ? piece === idx
                      ? 'border-green-400 bg-green-50 cursor-grab active:cursor-grabbing'
                      : 'border-amber-300 bg-amber-50 cursor-grab active:cursor-grabbing'
                    : 'border-dashed border-background-300/60 bg-background-100 cursor-pointer hover:border-primary-500/50'
                }`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDropOnGrid(idx)}
                onClick={() => handleClickGridPiece(idx)}
                title={piece !== null ? (piece === idx ? 'Piece correcte - cliquer pour retirer' : 'Piece mal placee - cliquer pour retirer') : `Position ${idx + 1}`}
              >
                {piece !== null ? (
                  <div
                    className={`w-full h-full rounded-lg overflow-hidden ${
                      piece === idx ? 'ring-2 ring-green-400' : 'ring-2 ring-amber-300'
                    }`}
                    draggable
                    onDragStart={() => handleDragStartGrid(idx)}
                    style={{
                      backgroundImage: `url(${PUZZLE_IMAGES[imageIdx]})`,
                      backgroundSize: `${gridSize * 100}%`,
                      backgroundPosition: `${(piece % gridSize) * (100 / (gridSize - 1))}% ${Math.floor(piece / gridSize) * (100 / (gridSize - 1))}%`,
                    }}
                  />
                ) : (
                  <span className="text-xs text-foreground-500">{idx + 1}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div
          className="w-full lg:w-48 flex-shrink-0"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDropOnBank}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground-950">Pieces</h3>
            <span className="text-xs text-foreground-600 bg-background-100 rounded-full px-2 py-0.5">{remainingCount}</span>
          </div>
          {remainingCount === 0 ? (
            <div className="bg-background-100 rounded-xl border-2 border-dashed border-background-300/60 p-4 text-center text-xs text-foreground-500">
              <i className="ri-inbox-line text-xl block mb-1"></i>
              Toutes les pieces sont placees.
              <br />
              Clique sur une piece de la grille pour la reprendre.
            </div>
          ) : (
            <div className="grid grid-cols-3 lg:grid-cols-2 gap-2 max-h-48 lg:max-h-[500px] overflow-y-auto p-1">
              {pieces.map((piece, idx) => {
                if (piece === -1) return <div key={idx} className="aspect-square" />;
                return (
                  <div
                    key={idx}
                    draggable
                    onDragStart={() => handleDragStartBank(idx)}
                    className="aspect-square bg-background-50 rounded-lg border-2 border-background-200 cursor-grab active:cursor-grabbing hover:border-primary-500/50 overflow-hidden hover:shadow-md transition-shadow"
                    style={{
                      backgroundImage: `url(${PUZZLE_IMAGES[imageIdx]})`,
                      backgroundSize: `${gridSize * 100}%`,
                      backgroundPosition: `${(piece % gridSize) * (100 / (gridSize - 1))}% ${Math.floor(piece / gridSize) * (100 / (gridSize - 1))}%`,
                    }}
                  />
                );
              })}
            </div>
          )}
          <p className="text-xs text-foreground-500 mt-2 text-center lg:text-left">
            <i className="ri-arrow-go-back-line mr-1"></i>
            Depose une piece ici pour la reprendre
          </p>
        </div>
      </div>

      {showGameOver && (
        <GameOverModal
          score={gameScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="puzzle-classique"
          message="Felicitations ! Puzzle reussi !"
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[usedTipIdx]}
        />
      )}
    </GameWrapper>
  );
}